<template>
  <simple-dialog ref="dlg" title="Register Member" width="750" :close-on-save="false" @ok="submit">
    <template v-slot:content>
      <template v-if="mode === 'incharge'">
        <v-autocomplete
          label="Search member (ITS, name, rank)"
          v-model="itsId"
          :items="memberOptions"
          item-title="label"
          item-value="itsId"
          :custom-filter="customFilter"
          :menu-props="{ maxHeight: 280 }"
          hide-details
          clearable
          class="mb-3"
        />
      </template>

      <template v-else>
        <v-text-field
          label="ITS ID"
          v-model.number="itsId"
          type="number"
          @keyup.enter="lookupMember"
        />
        <v-btn size="small" variant="outlined" class="mb-3 mr-2" @click="lookupMember">Look up member</v-btn>

        <v-alert v-if="member" type="info" variant="tonal" class="mb-3">
          <strong>{{ member.fullName }}</strong><br />
          Organization: {{ member.organizationName || member.organization?.name || "-" }}
        </v-alert>
      </template>

      <div class="mb-3" v-if="registrationCommitmentAmount > 0">
        <div class="text-body-2 text-medium-emphasis">Registration Amount</div>
        <div class="text-h5 font-weight-bold">{{ registrationCommitmentAmount }}</div>
      </div>

      <v-text-field
        v-if="niazCommitmentRequired"
        label="Niaz Commitment Amount"
        v-model.number="niazCommitment"
        type="number"
        suffix="USD"
        class="mb-3"
      />

      <div
        v-for="surveyForm in atRegistrationSurveys"
        :key="surveyForm.surveyId"
        class="mb-4"
      >
        <SurveyQuestionsForm
          :ref="(el) => setSurveyRef(surveyForm.surveyId, el)"
          :questions="surveyForm.questions || []"
          :title="surveyForm.surveyTitle"
          :description="surveyForm.description || ''"
          :required="surveyForm.isRequired"
          :allow-files="false"
        />
      </div>
    </template>
  </simple-dialog>
</template>

<script setup>
import { ref, inject, computed, reactive } from "vue";
import { getById } from "@/services/memberService";
import {
  registerMember,
  getRegisterableMembers,
  getRegistrationSurveyForms,
} from "@/services/miqaatService";
import SurveyQuestionsForm from "@/components/SurveyQuestionsForm.vue";

const props = defineProps({
  miqaatId: { type: Number, required: true },
  registrationCommitmentAmount: { type: Number, default: 0 },
  mode: { type: String, default: "admin" },
  niazCommitmentRequired: { type: Boolean, default: true },
  niazCommitmentCompulsory: { type: Boolean, default: true },
});

const emit = defineEmits(["registered"]);

const loader = inject("loader");
const snackbar = inject("snackBar");

const dlg = ref(null);
const itsId = ref(null);
const niazCommitment = ref(0);
const member = ref(null);
const members = ref([]);
const atRegistrationSurveys = ref([]);
const surveyRefs = reactive({});

const memberOptions = computed(() =>
  members.value.map((m) => ({
    itsId: m.itsId,
    fullName: m.fullName,
    rank: m.rank || "",
    label: `${m.itsId} - ${m.fullName} - ${m.rank || ""}`,
  }))
);

function setSurveyRef(surveyId, el) {
  if (el) surveyRefs[surveyId] = el;
  else delete surveyRefs[surveyId];
}

function customFilter(_itemTitle, queryText, item) {
  const q = (queryText || "").toLowerCase().trim();
  if (!q) return true;
  const m = item.raw;
  return (m.label || "").toLowerCase().includes(q)
    || (m.fullName || "").toLowerCase().includes(q)
    || String(m.itsId || "").includes(q)
    || (m.rank || "").toLowerCase().includes(q);
}

function resetForm() {
  itsId.value = null;
  niazCommitment.value = 0;
  member.value = null;
  members.value = [];
  Object.keys(surveyRefs).forEach((k) => delete surveyRefs[k]);
}

async function loadMembers() {
  if (props.mode !== "incharge") return;
  members.value = await getRegisterableMembers(props.miqaatId);
}

async function show() {
  resetForm();

  loader.show();
  try {
    atRegistrationSurveys.value =
      (await getRegistrationSurveyForms(props.miqaatId, "AtRegistration")) || [];
    if (props.mode === "incharge") {
      await loadMembers();
    }
  } catch (error) {
    snackbar.error(error);
    loader.hide();
    return;
  }
  loader.hide();

  dlg.value.show();
}

async function lookupMember() {
  if (!itsId.value) {
    snackbar.error("ITS ID is required");
    return;
  }

  loader.show();
  try {
    member.value = await getById(itsId.value);
  } catch (error) {
    member.value = null;
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

async function submit() {
  if (!itsId.value) {
    snackbar.error("Member is required");
    return;
  }

  const amount = props.niazCommitmentRequired ? niazCommitment.value : 0;
  if (props.niazCommitmentRequired && props.niazCommitmentCompulsory && !amount) {
    snackbar.error("Niaz commitment amount is required");
    return;
  }

  const surveyAnswers = [];
  for (const surveyForm of atRegistrationSurveys.value) {
    const ref = surveyRefs[surveyForm.surveyId];
    const collected = ref?.collectAnswers();
    if (!collected) return;
    surveyAnswers.push({
      surveyId: surveyForm.surveyId,
      answers: collected.answers,
    });
  }

  loader.show();
  try {
    await registerMember(props.miqaatId, {
      itsId: itsId.value,
      niazCommitment: amount,
      surveyAnswers,
    });
    snackbar.success("Member registered successfully");
    emit("registered");
    dlg.value.hide();
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

defineExpose({ show });
</script>
